import genericStyles from "../../Styles/genericStyles.module.css";
import { TablesIds, ENDPOINTS_SKIP_WORK_POINT_ID } from "../../Utils/Constants";
import { ETableEditAction, EComponentTypes } from "../../Utils/Enums";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";
import MUIDataTableCustomStyle from "../MUIDataTableCustomStyle";
import { formatDate } from "../../Utils/Utils";
import Filters from "../Widgets/Filters";
import { Filter } from "../../Utils/Types";
import * as yup from "yup";
import { useState, useEffect, useCallback, useMemo } from "react";
import { getData } from "../../Services/getData";
import useCustomMemo from "../../Hooks/useCustomMemo";
import useCustomSearchParam from "../../Hooks/useCustomSearchParam";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Typography,
  TextField,
} from "@mui/material";
import GenericModal from "../GenericModal";
import { updateData } from "../../Services/updateData";
import { postData } from "../../Services/postData";
import { useWorkPointFilterConfig } from "./workPointFilterUtils";
import withRole from "../../Hooks/withRole";
import { PhoneChip } from "../PhoneChip";
import { StatusChip } from "../StatusChip";

function CertificateExamsTable(props: any) {
  const [examOptions, setExamOptions] = useState<any[]>([]);
  const [selectedExamsData, setSelectedExamsData] = useState<any[]>([]);
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsMessage, setSmsMessage] = useState("");
  const memo = useCustomMemo();
  const customSearchParams = useCustomSearchParam();

  // Always call the hook, then conditionally use the result based on role restrictions
  const rawWorkPointFilterConfig = useWorkPointFilterConfig();
  const workPointFilterConfig = props.restrictByWorkPoint
    ? rawWorkPointFilterConfig
    : { workPointId: null, clientWorkPointId: null };

  // Filter order statuses for certificate types - memoized to prevent recalculation
  const certificateStatuses = useMemo(() => {
    return (
      memo?.storage?.orderStatuses?.filter(
        (status: any) =>
          status.typeId?.name === "Atestat" || status.typeId === "Atestat",
      ) || []
    );
  }, [memo?.storage?.orderStatuses]);

  // Generate year options from 2025 to current year
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2025; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  }, []);

  // Fetch exams when year or workpoint changes (single effect to avoid cascading updates)
  useEffect(() => {
    // Get year from URL params, default to current year
    const yearParam = customSearchParams.searchParams.get("year");
    const currentYear = new Date().getFullYear();
    const year = yearParam ? Number(yearParam) : currentYear;

    const startDate = `${year}-01-01`;
    const endDate = `${year}-12-31`;

    // Build query parameters based on workpoint filter configuration
    const examFilter = workPointFilterConfig.workPointId
      ? `&workPointId=${workPointFilterConfig.workPointId}`
      : "";

    const clientFilter = workPointFilterConfig.clientWorkPointId
      ? `&clientWorkPointId=${workPointFilterConfig.clientWorkPointId}`
      : "";

    const apiUrl = `${endpoints.exams.certificateExams}?fromDate=${startDate}&endDate=${endDate}${examFilter}${clientFilter}`;

    getData(apiUrl).then((response: any) => {
      if (response?.data) {
        const exams = response.data.map((exam: any) => ({
          ...exam,
          name: `${formatDate(exam.examDate)} - ${
            exam.workPointId?.name || ""
          }`,
          workPoint: exam.workPointId?.name,
        }));
        setExamOptions(exams);
      }
    });
  }, [
    workPointFilterConfig.workPointId,
    workPointFilterConfig.clientWorkPointId,
    customSearchParams.searchParams.get("year"),
  ]);

  /**
   * Filters configuration
   */
  const filters: Filter[] = [
    {
      property: "year",
      label: Vocabulary.year,
      type: EComponentTypes.YEAR_SELECT,
      validation: yup.mixed().nullable(),
      defaultValue: new Date().getFullYear(),
      options: yearOptions,
    },
    {
      property: "examId",
      label: Vocabulary.examDate,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: yup.mixed().nullable(),
      defaultValue: null,
      options: examOptions,
      autocompleteFilterProperty: "_id",
    },
    // Hidden date range filters calculated from year
    {
      property: "startDate",
      label: "Start Date",
      type: EComponentTypes.HIDDEN,
      validation: yup.string().nullable(),
      defaultValue: `${new Date().getFullYear()}-01-01`,
    },
    {
      property: "endDate",
      label: "End Date",
      type: EComponentTypes.HIDDEN,
      validation: yup.string().nullable(),
      defaultValue: `${new Date().getFullYear()}-12-31`,
    },
    // Hidden workpoint filters based on user's workpoint configuration
    ...(workPointFilterConfig.workPointId
      ? [
          {
            property: "workPointIdTest",
            label: "WorkPoint Filter",
            type: EComponentTypes.HIDDEN,
            validation: yup.string().nullable(),
            defaultValue: workPointFilterConfig.workPointId,
          },
        ]
      : []),
    ...(workPointFilterConfig.clientWorkPointId
      ? [
          {
            property: "clientWorkPointId",
            label: "Client WorkPoint Filter",
            type: EComponentTypes.HIDDEN,
            validation: yup.string().nullable(),
            defaultValue: workPointFilterConfig.clientWorkPointId,
          },
        ]
      : []),
  ];

  /**
   * Table columns configuration
   */
  const certificateExamsColumns = [
    {
      label: Vocabulary.id,
      name: "orderId",
      options: {
        sort: true,
        display: false,
        customBodyRender: (order: any) => {
          return order?._id || "";
        },
        exportValue: (order: any) => order?._id || "",
      },
    },
    {
      label: Vocabulary.workPoint,
      name: "workPointId",
      options: {
        sort: false,
        sortField: "workPoint.name",
        customBodyRender: (workPoint: any) => {
          return workPoint?.name || "";
        },
        exportValue: (workPoint: any) => workPoint?.name || "",
      },
    },

    {
      label: Vocabulary.examDate,
      name: "examDate",
      options: {
        sort: true,
        customBodyRender: (examDate: any) => {
          return examDate ? formatDate(examDate) : "";
        },
        exportValue: (examDate: any) => (examDate ? formatDate(examDate) : ""),
      },
    },
    {
      label: Vocabulary.status,
      name: "orderId",
      options: {
        sort: false,
        customBodyRender: (order: any) => {
          return <StatusChip status={order?.status} />;
        },
        exportValue: (order: any) => order?.status?.name || "",
      },
    },
    {
      label: Vocabulary.CNP,
      name: "client",
      options: {
        sort: false,
        customBodyRender: (client: any) => {
          return client?.CNP;
        },
        exportValue: (client: any) => client?.CNP || "",
      },
    },

    {
      label: Vocabulary.cursant,
      name: "client",
      options: {
        sort: false,
        customBodyRender: (client: any) => {
          return client?.lastName + " " + client?.firstName;
        },
        exportValue: (client: any) =>
          client?.lastName + " " + client?.firstName,
      },
    },
    {
      label: Vocabulary.product,
      name: "orderId",
      options: {
        sort: false,
        customBodyRender: (order: any) => {
          return order?.product?.name;
        },
        exportValue: (order: any) => order?.product?.name || "",
      },
    },
    {
      label: Vocabulary.rest,
      name: "orderId",
      options: {
        sort: false,
        customBodyRender: (order: any) => {
          if (order?.amountToPay) {
            return (
              <div style={{ color: "red", fontWeight: "bold" }}>
                {order?.amountToPay}
              </div>
            );
          }
          return order?.amountToPay;
        },
        exportValue: (order: any) => order?.amountToPay ?? "",
      },
    },

    {
      label: Vocabulary.phone,
      name: "client",
      options: {
        sort: false,
        customBodyRender: (client: any) => {
          return <PhoneChip phone={client?.phone} />;
        },
        exportValue: (client: any) => client?.phone || "",
      },
    },
    {
      label: Vocabulary.createdAt,
      name: "createdAt",
      options: {
        sort: true,
        customBodyRender: (createdAt: any) => {
          return createdAt ? formatDate(createdAt) : "";
        },
        exportValue: (createdAt: any) =>
          createdAt ? formatDate(createdAt) : "",
      },
    },
    /** Hidden (used for bulk sms) DON'T DELETE */
    {
      label: Vocabulary.clientId,
      name: "client",
      options: {
        sort: true,
        display: false,
        customBodyRender: (client: any) => {
          return client?._id;
        },
        exportValue: (client: any) => client?._id || "",
      },
    },
  ];

  /**
   * Handles bulk status update for selected exams and their related orders
   */
  const handleBulkStatusUpdate = useCallback(async () => {
    try {
      const updatePromises = selectedExamsData.map(async (examData: any) => {
        const orderId = examData[0];

        if (orderId)
          updateData(endpoints.ordersEndpoint + `/${orderId}`, {
            status: selectedStatus,
          });
      });

      await Promise.all(updatePromises);
      setBulkStatusDialogOpen(false);
      setSelectedStatus("");
      setSelectedExamsData([]);
      // Table will refresh automatically through the MUI datatable
    } catch (error) {
      console.error("Error updating exam statuses and related orders:", error);
    }
  }, [selectedExamsData, selectedStatus]);

  /**
   * Opens the bulk status update dialog and sets the selected exams data
   */
  const handleOpenBulkDialog = useCallback(
    (selectedRowsData: any, displayData: any) => {
      const selectedExams = selectedRowsData.data.map(
        (row: any) => displayData[row.dataIndex].data,
      );
      setSelectedExamsData(selectedExams);
      setBulkStatusDialogOpen(true);
    },
    [],
  );

  /**
   * Handles sending SMS to selected clients
   */
  const handleSendSms = useCallback(async () => {
    try {
      const clientIds = selectedExamsData.map(
        (examData: any) => examData[examData.length - 2],
      );

      await postData(endpoints.notifications.sendSMSMessages, {
        clientIds,
        message: smsMessage,
      });

      setSmsModalOpen(false);
      setSmsMessage("");
      setSelectedExamsData([]);
    } catch (error) {
      console.error("Error sending SMS:", error);
    }
  }, [selectedExamsData, smsMessage]);

  /**
   * Opens the SMS modal and sets the selected exams data
   */
  const handleOpenSmsModal = useCallback(
    (selectedRowsData: any, displayData: any) => {
      const selectedExams = selectedRowsData.data.map(
        (row: any) => displayData[row.dataIndex].data,
      );
      setSelectedExamsData(selectedExams);
      setSmsModalOpen(true);
    },
    [],
  );

  /**
   * Custom toolbar for bulk actions on selected rows
   */
  const customToolbarSelect = useCallback(
    (selectedRowsData: any, displayData: any) => {
      return (
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#1976D2",
              color: "white",
              fontWeight: 600,
              fontSize: "0.95rem",
              padding: "10px 24px",
              boxShadow: "0 2px 6px rgba(25, 118, 210, 0.3)",
              "&:hover": {
                backgroundColor: "#1565C0",
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.4)",
              },
            }}
            onClick={() => handleOpenBulkDialog(selectedRowsData, displayData)}
          >
            {`${Vocabulary.edit} ${Vocabulary.status} (${selectedRowsData.data.length})`}
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#4CAF50",
              color: "white",
              fontWeight: 600,
              fontSize: "0.95rem",
              padding: "10px 24px",
              boxShadow: "0 2px 6px rgba(76, 175, 80, 0.3)",
              "&:hover": {
                backgroundColor: "#45a049",
                boxShadow: "0 4px 12px rgba(76, 175, 80, 0.4)",
              },
            }}
            onClick={() => handleOpenSmsModal(selectedRowsData, displayData)}
          >
            {Vocabulary.sendSMS}
          </Button>
        </Box>
      );
    },
    [handleOpenBulkDialog, handleOpenSmsModal],
  );

  /**
   * Handles row click to navigate to order details in a new tab
   */
  const handleRowClick = useCallback((rowData: any) => {
    const orderId = rowData?.orderId?._id; // Get the orderId from the orderId field
    if (orderId) {
      const orderUrl = `${localUrlEnum.order}/${orderId}`;
      window.open(orderUrl, "_blank");
    }
  }, []);

  return (
    <div className={genericStyles.mainContainer}>
      <Filters filters={filters} defaultOpen />
      <MUIDataTableCustomStyle
        buttons={[]}
        onEditAction={ETableEditAction.MODAL}
        title={Vocabulary.certificateExams}
        id={TablesIds.examsTable}
        hideAddButton={true}
        columns={certificateExamsColumns}
        CRUDEndPoint={
          ENDPOINTS_SKIP_WORK_POINT_ID.certificateExamsClientsAssigned
        }
        hideEditButton={true}
        hideDeleteButton={true}
        selectableRows="multiple"
        customToolbarSelect={customToolbarSelect}
        onRowClick={handleRowClick}
        showPrintButton
        showDownloadButton
      />

      <GenericModal
        open={bulkStatusDialogOpen}
        onClose={() => setBulkStatusDialogOpen(false)}
        title={`${Vocabulary.edit} ${Vocabulary.status}`}
        width="600px"
        actions={
          <div className={genericStyles.modalActions}>
            <Button onClick={() => setBulkStatusDialogOpen(false)}>
              {Vocabulary.cancel}
            </Button>
            <Button
              onClick={handleBulkStatusUpdate}
              variant="contained"
              disabled={!selectedStatus}
            >
              {Vocabulary.save}
            </Button>
          </div>
        }
      >
        <Box sx={{ minWidth: 200, mt: 2 }}>
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "8px",
              "& .MuiAlert-icon": {
                color: "#856404",
              },
              "& .MuiAlert-message": {
                color: "#856404",
                fontWeight: 500,
              },
            }}
          >
            <Typography variant="body2">{Vocabulary.bulkWarning}</Typography>
          </Alert>
          <FormControl fullWidth>
            <InputLabel>{Vocabulary.status}</InputLabel>
            <Select
              value={selectedStatus}
              label={Vocabulary.status}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {certificateStatuses.map((status: any) => (
                <MenuItem key={status._id} value={status._id}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </GenericModal>

      <GenericModal
        open={smsModalOpen}
        onClose={() => {
          setSmsModalOpen(false);
          setSmsMessage("");
        }}
        title={Vocabulary.sendSMS}
        width="600px"
        actions={
          <div className={genericStyles.modalActions}>
            <Button
              onClick={() => {
                setSmsModalOpen(false);
                setSmsMessage("");
              }}
            >
              {Vocabulary.cancel}
            </Button>
            <Button
              onClick={handleSendSms}
              variant="contained"
              disabled={!smsMessage.trim()}
            >
              {Vocabulary.send}
            </Button>
          </div>
        }
      >
        <Box sx={{ minWidth: 200, mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {`${Vocabulary.cursant}i selectati (${selectedExamsData.length}):`}
          </Typography>
          <Box
            sx={{
              maxHeight: 150,
              overflowY: "auto",
              mb: 3,
              p: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
            }}
          >
            {selectedExamsData.map((examData: any, index: number) => {
              const client = examData[3];
              return (
                <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                  {client}
                </Typography>
              );
            })}
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={Vocabulary.message}
            value={smsMessage}
            onChange={(e) => setSmsMessage(e.target.value)}
            placeholder="Introduceți mesajul SMS..."
          />
        </Box>
      </GenericModal>
    </div>
  );
}

export default withRole(CertificateExamsTable);
